import {FC, useState} from 'react'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {InputText} from '@/components/commons/input-text/InputText'
import {GroupMember, createGroupMemberFromUser} from '@/features/users/types'
import {UserGroup, UserGroupFormValidationSchema} from '@/features/user-groups/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {InputSelectUser, UserSmallSelection} from '@/features/users/components/input-select-user/InputSelectUser'
import {StyledUsersContainer} from './style'
import {SelectedUserRow} from '@/features/user-groups/components/selected-user-row/SelectedUserRow'
import {useUpdateUserGroup} from '../../queries/useUpdateUserGroup'
import {QUERY_KEYS} from '@/queryClient'
import {useQueryClient} from '@tanstack/react-query'
import {useCreateUserGroup} from '../../queries/useCreateUserGroup'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {errorHandler} from '@/utilities/helpers'

interface UserGroupModalProps {
    onClose: () => void
    initialGroup?: UserGroup
}

export const UserGroupModal: FC<UserGroupModalProps> = ({onClose, initialGroup}) => {
    const {t} = useTranslation()
    const isEditing = !!initialGroup
    const queryClient = useQueryClient()

    // Initialize form with initial values
    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        watch
    } = useForm<UserGroupFormValidationSchema>({
        resolver: zodResolver(UserGroupFormValidationSchema),
        defaultValues: {
            name: initialGroup?.name || '',
            members: initialGroup?.members?.map(m => m.id) || []
        }
    })

    // Track selected users for display purposes
    const [selectedUsers, setSelectedUsers] = useState<GroupMember[]>(initialGroup?.members || [])
    const members = watch('members')

    const {mutate: createUserGroup, isPending: isCreatingUserGroup} = useCreateUserGroup({
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_GROUPS]})
            onClose()
        }
    })

    const {mutate: updateUserGroup, isPending: isUpdatingUserGroup} = useUpdateUserGroup({
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_GROUPS]})
            onClose()
        }
    })

    const onSubmit = (data: UserGroupFormValidationSchema) => {
        if (initialGroup) {
            updateUserGroup({
                urlParams: {groupId: initialGroup.id},
                payload: {
                    name: data.name,
                    members: data.members
                },
                params: {responseType: 'small'}
            })
        } else {
            createUserGroup({
                payload: {
                    name: data.name,
                    members: data.members
                },
                params: {responseType: 'small'}
            })
        }
    }

    const handleAddUser = (user: UserSmallSelection | null) => {
        if (!user || members.includes(user.id)) return

        setValue('members', [...members, user.id], {shouldValidate: true})
        setSelectedUsers(prev => [
            ...prev,
            createGroupMemberFromUser({
                id: user.id,
                fullName: user.fullName,
                email: user.email ?? '',
                avatarUrl: user.avatarUrl ?? null
            })
        ])
    }

    const handleRemoveUser = (userId: string) => {
        setValue(
            'members',
            members.filter(id => id !== userId),
            {shouldValidate: true}
        )
        setSelectedUsers(prev => prev.filter(user => user.id !== userId))
    }

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="600px" style={{overflow: 'visible'}}>
                <ModalHeader>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <ModalTitle>{isEditing ? t('user_groups:edit_group') : t('user_groups:add_group')}</ModalTitle>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                </ModalHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <Flexbox direction="column" gap={4} fullWidth>
                            <InputText
                                id="group-name"
                                label={t('user_groups:group_name_input_label')}
                                type="text"
                                placeholder={t('user_groups:group_name_placeholder')}
                                errorMessage={errors.name?.message}
                                {...register('name')}
                            />

                            <Flexbox direction="column" gap={2} fullWidth>
                                <InputSelectUser
                                    {...register('members')}
                                    value={null}
                                    onChange={handleAddUser}
                                    placeholder={t('commons:search_placeholder')}
                                    disabledOptions={members}
                                    errorMessage={errors.members?.message}
                                />

                                {selectedUsers.length > 0 && (
                                    <StyledUsersContainer>
                                        {selectedUsers.map(user => (
                                            <SelectedUserRow key={user.id} user={user} onRemove={handleRemoveUser} />
                                        ))}
                                    </StyledUsersContainer>
                                )}
                            </Flexbox>
                        </Flexbox>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={onClose} type="button">
                            <Text size="textMd">{t('commons:cancel')}</Text>
                        </Button>
                        <Button
                            type="submit"
                            variant="primaryNeutral"
                            disabled={isCreatingUserGroup || isUpdatingUserGroup}
                        >
                            <Text size="textMd">{isEditing ? t('commons:save') : t('user_groups:add_group')}</Text>
                            {isCreatingUserGroup || (isUpdatingUserGroup && <Spinner />)}
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>
        </>
    )
}
